/**
 * @description High Order Component для оборачивания контента модалки
 * @params {string} title - заголовок модального окна
 * @params {object} children - реакт-компоненты контента модалки
 */
import clsx from 'clsx'
import { Grid, PaymentLogo, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { CloseButton, CloseLine } from 'components/Modal/subcomponents'
import styles from 'components/Modal/modal.module.scss'
import { APP_PRODUCT } from 'constants/app-config'

const ModalFrameHoc = ({
  className = '',
  children,
  closeModal,
  showCloseButton = true,
  title,
  noHeader,
  variant,
}) => {
  const [isMouseDown, setMouseDown] = useState(null)
  const [startPosY, setStartPosY] = useState(0)
  const [currentPosY, setCurrentPosY] = useState(false)

  const handleMouseDown = useCallback((e) => {
    setMouseDown(true)
    setStartPosY(e.clientY || e.touches[0].clientY)
  }, [])

  const handleTransitionEnd = useCallback(
    () => isMouseDown === false && closeModal(),
    [isMouseDown],
  )

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isMouseDown) {
        return
      }
      const pos = e.clientY || e.touches[0].clientY - startPosY

      setCurrentPosY((pos > 0 ? pos : 0) + 'px')
    }

    const handleMouseUp = () => isMouseDown && setMouseDown(false)

    document.addEventListener('mouseup', handleMouseUp)
    document.addEventListener('touchend', handleMouseUp)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('touchmove', handleMouseMove)

    return () => {
      document.removeEventListener('mouseup', handleMouseUp)
      document.removeEventListener('touchend', handleMouseUp)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('touchmove', handleMouseMove)
    }
  }, [startPosY, setMouseDown, setCurrentPosY, isMouseDown])

  useEffect(() => {
    if (isMouseDown === false) {
      setCurrentPosY('100%')
    }
  }, [isMouseDown])

  return (
    <>
      {variant === 'fullViewport' && (
        <Grid
          className={styles['fixed-header']}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <PaymentLogo
            name={APP_PRODUCT ?? 'getuniq'}
            type={'inner'}
            color={'basic-primary-black'}
            width={40}
          />
          <CloseButton
            onClick={closeModal}
            className={styles['fixed-header__close-btn']}
          />
        </Grid>
      )}
      <Grid
        direction={'column'}
        className={'content-wrapper'}
        onTransitionEnd={handleTransitionEnd}
        style={
          variant === 'bottomSheet'
            ? {
                transform: `translateY(${currentPosY})`,
                transition: isMouseDown === true ? 'none' : '',
              }
            : {}
        }
      >
        {variant === 'bottomSheet' && (
          <CloseLine
            onMouseDown={handleMouseDown}
            onTouchStart={handleMouseDown}
          />
        )}
        <div className={clsx('modal-content', className)}>
          {noHeader ? (
            showCloseButton &&
            variant !== 'fullViewport' && <CloseButton onClick={closeModal} />
          ) : (
            <div className="modal-header">
              {!!title && (
                <Typography variant="Header-50" noMargin>
                  {title}
                </Typography>
              )}

              {showCloseButton && variant !== 'fullViewport' && (
                <CloseButton onClick={closeModal} />
              )}
            </div>
          )}

          <div className={clsx('modal-body', styles.body)}>{children}</div>
        </div>
      </Grid>
    </>
  )
}

ModalFrameHoc.propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
  ]),
  closeModal: PropTypes.func,
  showCloseButton: PropTypes.bool,
  title: PropTypes.string,
  noHeader: PropTypes.bool,
  variant: PropTypes.string,
}

export default ModalFrameHoc
